import { Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { useState } from "react"

import { TRANSFER_DOCUMENT_TYPE_TO_CHIP } from "../../constants"
import { useGetTransferDocuments } from "../../hooks/useGetTransferDocuments"
import { canModifyTransfer } from "../../utils"

import { Button } from "@/components/common/Button"
import ConfirmationDialog from "@/components/common/Modals/ConfirmationDialog"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { FilesTable } from "@/components/Tables/Aeros/components/FilesTable"
import { useFileColumns } from "@/components/Tables/Aeros/hooks/useFileColumns"
import type { SignedUrl, Transfer } from "@/graphql/codegen/graphql"
import { useFilesRemove } from "@/hooks/useFilesRemove"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface TransferDocumentsDialogProps {
  transfer: Transfer
  onClose: () => void
}

export const TransferDocumentsDialog: FC<TransferDocumentsDialogProps> = ({ transfer, onClose }) => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [deletingDocument, setDeletingDocument] = useState<SignedUrl>()

  const { documents, isFetching, refetch } = useGetTransferDocuments(transfer.transferId)
  const { removeSingleFile } = useFilesRemove(refetch)

  const handleDeleteConfirm = async () => {
    const fileId = deletingDocument?.file?.fileId
    setDeletingDocument(undefined)
    if (!fileId) return

    const isDeleted = await removeSingleFile(fileId)
    notify(
      isDeleted
        ? { type: NotificationType.Success, message: "Document deleted" }
        : { type: NotificationType.Error, message: "Failed to delete document" }
    )
  }

  const columns = useFileColumns({
    typeToChip: TRANSFER_DOCUMENT_TYPE_TO_CHIP,
    canDelete: canModifyTransfer(transfer),
    onDelete: setDeletingDocument,
  })

  return deletingDocument ? (
    <ConfirmationDialog
      title='Delete Document'
      content='Are you sure you want to delete this document? This action cannot be undone.'
      closeLabel='Cancel'
      confirmLabel='Delete'
      isWarning
      onClose={() => setDeletingDocument(undefined)}
      onConfirm={handleDeleteConfirm}
    />
  ) : (
    <Dialog open onClose={onClose} maxWidth='md' fullWidth data-testid='transfer.documents-dialog'>
      <DialogHeader title='Documents' onClose={onClose} />
      <DialogContent className='p-10'>
        <FilesTable data={documents} isLoading={isFetching} columns={columns} />
      </DialogContent>
      <DialogActions className='flex items-center justify-end px-10 pb-10'>
        <Button appearance='contained' onClick={onClose} data-testid='transfer.documents-dialog.close-button'>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
